/**
 * Shared types for Action Proposal Drawer (A.7).
 *
 * The drawer state machine is the contract between the shell, the editor,
 * and the wire-up layer in `apps/client/src/features/actions/`.
 *
 * State diagram (per `ando_ui_v2_rewrite_plan.md` §A.7):
 *
 *   proposed ──edit──▶ editing ──confirm──▶ confirming ──run──▶ executing
 *        ▲                │                       │                 │
 *        │                │                       │                 ├─▶ executed
 *        └──cancel────────┘◀──────cancel──────────┘                 │
 *                                                                    └─▶ error
 *
 * `editing` is a soft state: a single field commit transitions
 * `proposed` → `editing`. ESC during `editing` reverts to `proposed`
 * (handled by the consumer via the `onRevert` callback).
 */

export type ActionDrawerStatus =
  | "proposed"
  | "editing"
  | "confirming"
  | "executing"
  | "executed"
  | "error";

export type ActionDrawerEvent =
  | { kind: "edit" }
  | { kind: "revert" }
  | { kind: "confirm" }
  | { kind: "cancel_confirm" }
  | { kind: "execute_start" }
  | { kind: "execute_success" }
  | { kind: "execute_failure"; error: string }
  | { kind: "retry" };

export interface ActionDrawerState {
  status: ActionDrawerStatus;
  error: string | null;
}

export const INITIAL_ACTION_STATE: ActionDrawerState = {
  status: "proposed",
  error: null,
};

/**
 * Pure reducer — exported so unit tests can drive the state machine
 * directly without rendering.
 */
export function actionDrawerReducer(
  state: ActionDrawerState,
  event: ActionDrawerEvent,
): ActionDrawerState {
  switch (event.kind) {
    case "edit":
      // Only `proposed` and `editing` accept further edits; ignore otherwise.
      if (state.status === "proposed") return { status: "editing", error: null };
      if (state.status === "editing") return state;
      return state;
    case "revert":
      // ESC during editing reverts to proposed (cancellable per spec).
      if (state.status === "editing") return { status: "proposed", error: null };
      return state;
    case "confirm":
      if (state.status === "proposed" || state.status === "editing") {
        return { status: "confirming", error: null };
      }
      return state;
    case "cancel_confirm":
      if (state.status === "confirming") {
        // Drop back to `editing` if there were edits, else `proposed`.
        return { status: "editing", error: null };
      }
      return state;
    case "execute_start":
      if (state.status === "confirming" || state.status === "error") {
        return { status: "executing", error: null };
      }
      return state;
    case "execute_success":
      if (state.status === "executing") return { status: "executed", error: null };
      return state;
    case "execute_failure":
      if (state.status === "executing") {
        return { status: "error", error: event.error };
      }
      return state;
    case "retry":
      if (state.status === "error") return { status: "executing", error: null };
      return state;
    default:
      return state;
  }
}
